/* eslint-disable */
import {
  Accordion,
  Box,
  Button,
  Group,
  List,
  Select,
  Stack,
  Text,
  Divider,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import React, { useState } from "react";
import { ExclamationMark } from "tabler-icons-react";

import { useQueryClient } from "@tanstack/react-query";
import { useUpdateUserRole } from "api/users/updateUserRole";
import { User } from "types";
import { PermissionAccordionEditor } from "./permissionaccordian";
import { useGetDepartments } from "api/department/getAllDepartment";
import { useGetProgramsByDepartment } from "api/program/getProgramsByDepartment";
import { useGetRolePermissions } from "api/role/getRoles";
import { formatRoleName } from "utils/formatRoleName";
import { AdminPermissionScopesModal } from "./AdminPermissionScopesModal";
import { PermissionScopeBadge } from "./PermissionScopeBadge";

type EditRoleFormProps = {
  onClose: () => void;
  member: User<string>;
  orgId: string;
};

function EditRoleForm({ onClose, member, orgId }: EditRoleFormProps) {
  const queryClient = useQueryClient();
  const { data: departmentsData } = useGetDepartments();
  const { data: rolesData } = useGetRolePermissions();
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(
    member.department?._id || null,
  );
  const { data: programsData } = useGetProgramsByDepartment(selectedDepartment);

  // State for scope modal - initialize with existing scopes from member
  const [scopeModalOpened, setScopeModalOpened] = useState(false);
  const [enabledAdminPermissions, setEnabledAdminPermissions] = useState<Array<'attendance.admin' | 'employees.adminLeave'>>([]);
  const [adminPermissionScopes, setAdminPermissionScopes] = useState<Record<'attendance.admin' | 'employees.adminLeave', 'department' | 'program' | 'user'>>(
    (member.adminPermissionScopes as any) || ({} as Record<'attendance.admin' | 'employees.adminLeave', 'department' | 'program' | 'user'>)
  );

  const form = useForm({
    initialValues: {
      role: member.role,
      department: member.department?._id || "",
      program: member.program?._id || "",
    },
  });

  const departmentsRaw = departmentsData?.data ?? departmentsData ?? [];
  const departments: any[] = Array.isArray(departmentsRaw)
    ? departmentsRaw
    : [];

  const departmentOptions =
    departments?.map((d) => ({
      value: d._id as string,
      label: d.name as string,
    })) ?? [];

  const programsRaw = programsData?.data ?? [];
  const programs: any[] = Array.isArray(programsRaw) ? programsRaw : [];
  const programOptions =
    programs?.map((p) => ({
      value: p._id as string,
      label: p.name as string,
    })) ?? [];

  // Get dynamic roles from organization
  const rolesRaw = rolesData?.data ?? [];
  const roleOptions =
    rolesRaw?.map((r: any) => ({
      value: r.role as string,
      label: formatRoleName(r.role as string),
    })) ?? [];

  const hasPrograms = programs && programs.length > 0;

  const updateUserRoleMutation = useUpdateUserRole(orgId);

  type FormValues = typeof form.values;

  const handleDepartmentChange = (value: string | null) => {
    form.setFieldValue("department", value || "");
    setSelectedDepartment(value);
    // Reset program field when department changes
    form.setFieldValue("program", "");
  };

  // Get admin permissions from a role
  const getAdminPermissionsFromRole = (roleName: string): Array<'attendance.admin' | 'employees.adminLeave'> => {
    const role = rolesRaw?.find((r: any) => r.role === roleName);
    if (!role || !role.permissions) return [];

    const adminPermissions: Array<'attendance.admin' | 'employees.adminLeave'> = [];
    if (role.permissions?.attendance?.admin === true) {
      adminPermissions.push('attendance.admin');
    }
    if (role.permissions?.employees?.adminLeave === true) {
      adminPermissions.push('employees.adminLeave');
    }

    return adminPermissions;
  };

  // Handle role change - show modal if new role has admin permissions
  const handleRoleChange = (newRole: string | null) => {
    if (!newRole) return;

    form.setFieldValue("role", newRole as any);

    // Get admin permissions from the new role
    const adminPerms = getAdminPermissionsFromRole(newRole);

    if (adminPerms.length > 0) {
      setEnabledAdminPermissions(adminPerms);
      setScopeModalOpened(true);
    }
  };

  const handleSubmit = (values: FormValues) => {
    // Build data to submit
    const dataToSubmit: any = {
      role: values.role,
    };

    if (values.department && values.department.trim() !== "") {
      dataToSubmit.department = values.department;
    }

    if (values.program && values.program.trim() !== "") {
      dataToSubmit.program = values.program;
    }

    updateUserRoleMutation.mutate(
      {
        userId: member._id,
        userData: dataToSubmit,
      },
      {
        onSuccess: () => {
          // Role changes reset this user's permissions to the new role's
          // defaults server-side (createUserPermissionsOnSignup) — without
          // this, a cached permissions query would show stale pre-change
          // values the next time this modal (or the accordion) is opened.
          queryClient.invalidateQueries(["userPermissions", member._id]);
          onClose();
        },
        onError: () => {
          showNotification({
            title: "Error",
            message: "Failed to update role. Please try again.",
            color: "red",
            icon: <ExclamationMark />,
          });
        },
      },
    );
  };

  const handleScopeConfirm = (scopes: Record<'attendance.admin' | 'employees.adminLeave', 'department' | 'program' | 'user'>) => {
    setAdminPermissionScopes(scopes);
    setScopeModalOpened(false);
  };

  return (
    <>
      <AdminPermissionScopesModal
        opened={scopeModalOpened}
        onClose={() => setScopeModalOpened(false)}
        onConfirm={handleScopeConfirm}
        enabledPermissions={enabledAdminPermissions}
        existingScopes={adminPermissionScopes}
        isLoading={updateUserRoleMutation.isLoading}
      />

      {/* <Accordion
        variant="contained"
        order={4}
        defaultValue={member.role && [member.role]}
        multiple
        styles={{
          label: {
            fontWeight: 600,
          },
        }}
      >
        <Accordion.Item value="admin">
          <Accordion.Control>Admin</Accordion.Control>
          <Accordion.Panel>
            <Text size={14}>Access to:</Text>
            <List size={14} withPadding>
              <List.Item>Dashboard</List.Item>
              <List.Item>Projects</List.Item>
              <List.Item>Project Management</List.Item>
              <List.Item>Admin Settings</List.Item>
            </List>
          </Accordion.Panel>
        </Accordion.Item>
        <Accordion.Item value="project manager">
          <Accordion.Control>Project Manager</Accordion.Control>
          <Accordion.Panel>
            <Text size={14}>Access to:</Text>
            <List size={14} withPadding>
              <List.Item>Dashboard</List.Item>
              <List.Item>Projects</List.Item>
              <List.Item>Project Management</List.Item>
            </List>
          </Accordion.Panel>
        </Accordion.Item>
        <Accordion.Item value="member">
          <Accordion.Control>Member</Accordion.Control>
          <Accordion.Panel>
            <Text size={14}>Access to:</Text>
            <List size={14} withPadding>
              <List.Item>Dashboard</List.Item>
              <List.Item>Projects</List.Item>
            </List>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion> */}

      <Box
        style={{
          maxHeight: 450,
          overflowY: "auto",
          marginBottom: 16,
          paddingRight: 4,
        }}
      >
        <PermissionAccordionEditor memberId={member._id} />
      </Box>

      {/* Display Current Permission Scopes */}
      {adminPermissionScopes && Object.keys(adminPermissionScopes).length > 0 && (
        <>
          <Divider my="md" />
          <Box mb="md">
            <Text weight={600} size="sm" mb="sm">
              Admin Permission Scopes
            </Text>
            <Stack spacing="xs">
              {(['attendance.admin', 'employees.adminLeave'] as const).map((perm) => {
                if (adminPermissionScopes[perm]) {
                  return (
                    <Group key={perm} position="apart">
                      <Text size="sm">
                        {perm === 'attendance.admin' ? 'Attendance Admin' : 'Admin Leave'}
                      </Text>
                      <PermissionScopeBadge
                        permission={perm}
                        scope={adminPermissionScopes[perm]}
                        onEdit={() => {
                          setEnabledAdminPermissions([perm]);
                          setScopeModalOpened(true);
                        }}
                      />
                    </Group>
                  );
                }
                return null;
              })}
            </Stack>
          </Box>
        </>
      )}

      <form onSubmit={form.onSubmit(handleSubmit)}>
        {/* <Select
          label="Department"
          data={departmentOptions}
          size="md"
          searchable
          value={form.values.department || ""}
          onChange={handleDepartmentChange}
          mt={20}
        />
        {hasPrograms && (
          <Select
            label="Program"
            data={programOptions}
            size="md"
            searchable
            mt={20}
            {...form.getInputProps("program")}
          />
        )} */}
        <Select
          label="Role"
          placeholder="Select a role"
          searchable
          data={roleOptions}
          mt={20}
          size="md"
          value={form.values.role || ""}
          onChange={handleRoleChange}
        />
        <Group position="right" spacing={20} mt={30}>
          <Button color="cancel" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!form.isDirty()}
            loading={
              updateUserRoleMutation.isLoading ||
              updateUserRoleMutation.isSuccess
            }
          >
            Update
          </Button>
        </Group>
      </form>
    </>
  );
}

export default EditRoleForm;
